<template>
  <div>
    <b-container class="wide">
      <head-block :title="$t('title.customer.products')" />
      <b-row align-h="between">
        <b-col
          class="align-self-center mb-2"
          cols="auto"
        >
          <search-field
            v-model="searchString"
            @search="getUserProducts"
            :search-placeholder="$t('table.general.search_products')"
          />
        </b-col>
        <b-col
          cols="auto"
        >
          <b-button
            class="mr-2 mb-2"
            @click="includeOnlySoldProducts"
            variant="success">
            {{ $t('filter.by.products.include_only_sold_products') }}
          </b-button>
          <b-button
            class="mr-2 mb-2"
            @click="includeOnlyNonSoldProducts"
            variant="secondary">
            {{ $t('filter.by.products.include_only_non_sold_products') }}
          </b-button>
          <b-button
            class="mr-2 mb-2"
            @click="includeAllProducts"
            variant="info">
            {{ $t('filter.by.products.include_all_products') }}
          </b-button>
        </b-col>
      </b-row>
      <content-table
        :fields="fields"
        :items="userProducts"
        :slots="templates"
        :primary-key="primaryKey"
        head-variant="dark"
      >
        <template slot="status" slot-scope="data">
          <div class="d-flex justify-content-center">
            {{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}
          </div>
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            v-if="soldStatuses.includes(data.tbl.item.status) || data.tbl.item.hasSaleLines"
            size="sm"
            v-b-tooltip.hover.bottomleft
            :title="$t('table.products.button_view_product_sales')"
            variant="info"
            :to="{name: 'product-sale-lines', params: { productSheetId: data.tbl.item.productSheet.id, productId: data.tbl.item.id }}">
            <b-icon icon="view-stacked"></b-icon>
          </b-button>
        </template>
      </content-table>
      <span v-if="!loader.isActive && !userProducts.length" class="no-table-data">
        {{ $t('table.general.no_data') }}
      </span>
      <pagination
        :page="page"
        :pages-count="pagesCount"
        :page-size="pageSize"
        :count="count"
        @page-change="clickPaginationCallback"
        @page-size-change="onPageSizeChange" />
    </b-container>
  </div>
</template>

<script>

  import { mapGetters, mapState } from 'vuex'
  import ContentTable from '@base/components/ContentTable'
  import Pagination from '@base/components/Pagination.vue'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import ObjectUtils from '@base/mixins/ObjectUtils.vue'
  import {
    EDITABLE_STATUSES,
    SOLD_STATUSES,
    PRODUCT_DRAFT,
    PRODUCT_STATUSES,
    PRODUCT_SOLD,
    PRODUCT_READY, PRODUCT_PROCESSING, PRODUCT_ACTIVE, PRODUCT_PRINTED
  } from '@base/configs'
  import SearchField from '@base/components/SearchField.vue'

  export default {
    name: 'ProductsView',

    components: {
      SearchField,
      HeadBlock,
      ContentTable,
      Pagination,
    },

    mixins: [ ObjectUtils ],

    data() {
      return {
        primaryKey: 'number',
        sortBy: 'number',
        sortDesc: false,
        templates: [
          {name: 'status', field: 'status'},
          {name: 'actions', field: 'actions'},
        ],
        loading: false,
        searchString: null,
        filters: {},
        showFilter: true,
        productStatuses: PRODUCT_STATUSES,
        editableStatuses: EDITABLE_STATUSES,
        soldStatuses: SOLD_STATUSES,
        statusDraft: PRODUCT_DRAFT,
      }
    },

    created() {
      this.getUserProducts()
    },

    computed: {
      ...mapState(['userProducts', 'userMarket']),
      ...mapGetters({
        page: 'getUserProductsPage',
        pageSize: 'getUserProductsPageSize',
        pagesCount: 'getUserProductsPagesCount',
        count: 'getUserProductsCount'
      }),

      barcodeEnabled () {
        return !!(this.userMarket?.zettleIntegration || this.userMarket?.isFlealoverSoloFeature)
      },

      fields () {
        const fieldsArray = [
          {key: 'number', label: this.$t('table.products.number'), class: 'text-center number-thead-width'},
          {key: 'name', label: this.$t('table.products.name'), class: 'text-center name-thead-width'},
          {key: 'originalPrice', label: this.$t('table.products.price'), class: 'text-center price-thead-width'},
          {key: 'sellPrice', label: this.$t('table.products.sell_price'), class: 'text-center price-thead-width'},
          {key: 'description', label: this.$t('table.products.description'), class: 'text-center price-thead-width'},
          {key: 'status', label: this.$t('table.products.status'), class: 'text-center'},
          {key: 'barcode', label: this.$t('table.products.barcode'), class: 'text-center'},
          { key: 'actions', label: this.$t('form.general.actions'), class: 'text-center' }
        ]
        if (!this.barcodeEnabled) {
          return fieldsArray.filter(field => field.key !== 'barcode')
        }
        return fieldsArray
      }
    },

    methods: {

      getUserProducts () {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, 1)
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_FILTERS, { search: this.searchString, ...this.filters })
        this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_LIST)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      clickPaginationCallback (pageNum) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, pageNum)
        this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_LIST)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      onPageSizeChange (size) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.dispatch(this.$_actionTypes.CHANGE_TABLE_PAGE_SIZE, size)
        this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_LIST)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PRODUCT_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      includeOnlySoldProducts () {
        this.filters = {
          status: [
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_SOLD).code,
          ],
        }
        this.updateProductsWithFilter()
      },
      includeOnlyNonSoldProducts () {
        this.filters = {
          status: [
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_DRAFT).code,
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_READY).code,
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_PROCESSING).code,
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_ACTIVE).code,
            PRODUCT_STATUSES.find(obj => obj.code === PRODUCT_PRINTED).code,
          ],
        }
        this.updateProductsWithFilter()
      },

      includeAllProducts () {
        this.filters = {}
        this.updateProductsWithFilter()
      },

      updateProductsWithFilter () {
        this.$store.commit(this.$_mutationTypes.SET_PRODUCT_PAGE, 1)
        this.getUserProducts()
      },
    }
  }

</script>

<style>

  .number-thead-width {
    min-width: 50px;
    max-width: 135px;
  }
  .name-thead-width {
    max-width: 250px;
  }
  .price-thead-width {
    max-width: 100px;
  }
  .discount-thead-width {
    max-width: 80px;
  }

</style>
